<template>
    <b-card footer-class="p-0" class="side-tab">
        <div v-if="isLoading">
            <loader-custom />
        </div>
        <div v-else class="row tab-header">
            <div class="col-lg-6 d-sm-flex title">
                <h2 class="mb-0">{{ role.name }}</h2>
                <div class="ml-2">
                    <strong>{{
                        role.staff_count +
                        " " +
                        (role.staff_count > 1 ? "users" : "user")
                    }}</strong>
                    {{ " " + (role.staff_count > 1 ? "are" : "is") }}
                    assigned to this role.
                </div>
            </div>
            <div v-if="lastUpdatedDurationText" class="col-lg-6 updated">
                Last updated {{ lastUpdatedDurationText }} ago
            </div>
        </div>
        <hr class="tab-separator" />
        <div v-if="isLoading || modules.length" class="tab-body row">
            <div v-if="isLoading" class="col-md-2 tabs d-none d-md-block">
                <loader-custom :rowCount="9" />
            </div>
            <div v-else class="col-md-2 tabs d-none d-md-block">
                <div
                    class="text"
                    v-for="(module, moduleIndex) in modules"
                    :key="module.module"
                    :id="'nav-' + module.module"
                    :class="{ active: moduleIndex == activeModule }"
                    @click="scrollToModule(module.module)"
                >
                    {{ sanitizeTitle(module.module) }}
                </div>
            </div>
            <div v-if="isLoading" class="col-md-10 details">
                <loader-list />
            </div>
            <div v-else id="tab-details" class="col-md-10 details">
                <div>
                    <div
                        v-for="(permission, permissionKey) in permissions"
                        :key="permissionKey"
                        class="mb-5 module-permission-group"
                    >
                        <div class="title" :ref="permissionKey.toLowerCase()">
                            <h2 class="mb-0">
                                {{ sanitizeTitle(permissionKey) }}
                            </h2>
                        </div>
                        <div class="group-holder card-columns">
                            <div
                                class="card"
                                v-if="permission.permissions.length"
                            >
                                <div
                                    v-for="(
                                        item, itemKey
                                    ) in permission.permissions"
                                    :key="item.key"
                                    :class="[itemKey === 0 ? 'mt-3' : '']"
                                >
                                    <permissions-checkbox
                                        :permission="item"
                                        :updatePermission="updatePermission"
                                    />
                                </div>
                            </div>

                            <div
                                class="card mr-2"
                                v-for="(
                                    subPermission, subPermissionKey
                                ) in permission.submodules"
                                :key="subPermissionKey"
                            >
                                <h4 class="mt-3 mb-2">
                                    {{ sanitizeTitle(subPermissionKey) }}
                                </h4>
                                <div
                                    v-for="subItem in subPermission.permissions"
                                    :key="subItem.key"
                                >
                                    <permissions-checkbox
                                        :permission="subItem"
                                        :updatePermission="updatePermission"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="no-permission-found">
            <p class="m-0">
                Keyword in permissions not found. Please try a different search
                term.
            </p>
        </div>
    </b-card>
</template>
<script>
import { forEach, inRange } from "lodash";
import LoaderCustom from "@/components/elements/loader-custom";
import LoaderList from "@/components/elements/loader-list";
import permissionsCheckbox from "@/components/setting-permissions/permissions-checkbox";
import { capitalize } from "lodash";

export default {
    components: {
        LoaderCustom,
        LoaderList,
        permissionsCheckbox
    },
    props: {
        isParentLoading: {
            type: Boolean,
            default: true
        },
        role: {
            type: Object,
            default: null
        },
        modules: {
            type: Array,
            default: () => []
        },
        permissions: {
            type: Object,
            default: null
        },
        lastUpdatedDuration: {
            type: Object,
            default: null
        },
        updatePermission: {
            type: Function
        }
    },
    data() {
        return {
            isLoading: true,
            scrollTop: 0,
            activeModule: 0,
            titlePositions: []
        };
    },
    computed: {
        lastUpdatedDurationText() {
            let text = "";
            if (this.lastUpdatedDuration) {
                const { seconds, minutes } = this.lastUpdatedDuration;

                if ((seconds && seconds < 2) || seconds === 0) {
                    text = "a second";
                } else if (seconds) {
                    text = `${Math.trunc(seconds)} seconds`;
                } else if (minutes && minutes < 2) {
                    text = "a minute";
                } else if (minutes) {
                    text = `${Math.trunc(minutes)} minutes`;
                }
            }

            return text;
        }
    },
    beforeDestroy() {
        const tabDetails = document.getElementById("tab-details");
        if (this.permissions && tabDetails) {
            tabDetails.removeEventListener("scroll", this.handleScroll, true);
        }
    },
    watch: {
        isParentLoading(newVal) {
            this.isLoading = newVal;
            this.activeModule = 0;
            this.firstLoad();
        },
        scrollTop(newVal) {
            let self = this;

            forEach(this.titlePositions, function (value, key) {
                //handle last field
                if (key === self.titlePositions.length - 1) {
                    if (newVal >= value) {
                        self.activeModule = key;
                        return false;
                    }
                } else if (
                    inRange(newVal, value, self.titlePositions[key + 1])
                ) {
                    self.activeModule = key;
                    return false;
                }
            });
        },
        permissions() {
            this.firstLoad();
        }
    },
    methods: {
        firstLoad() {
            if (!this.isLoading) {
                this.titlePositions = [];
                this.scrollTop = 0;

                if (this.permissions && this.modules) {
                    this.initTabDetails();
                }
            }
        },
        initTabDetails() {
            this.$nextTick(() => {
                const tabDetails = document.getElementById("tab-details");

                if (tabDetails) {
                    tabDetails.addEventListener(
                        "scroll",
                        this.handleScroll,
                        true
                    );

                    this.getModuleOffset();
                }
            });
        },
        getModuleOffset() {
            let self = this;
            let titles = document
                .getElementById("tab-details")
                .getElementsByClassName("title");

            forEach(titles, function (value, key) {
                self.modules[key]["position"] = value.offsetTop;
                if (key > 0) {
                    self.titlePositions.push(value.offsetTop - 40);
                    // -5 add some space before reach the title
                } else {
                    self.titlePositions.push(value.offsetTop);
                }
            });
        },
        handleScroll(event) {
            this.scrollTop = event.currentTarget.scrollTop;
        },
        scrollToModule(refName) {
            var element = this.$refs[refName];

            if (element) {
                var top = element[0].offsetTop;
                document.getElementById("tab-details").scrollTo(1, top);
            }
        },
        sanitizeTitle(key) {
            let sanitizedTitle = capitalize((key || "").replaceAll("_", " "));

            // NOTE: Add missing plural s for certain module
            if (
                ["Patient", "Client", "Contact", "Report"].includes(
                    sanitizedTitle
                )
            ) {
                sanitizedTitle += "s";
            }

            return sanitizedTitle;
        }
    }
};
</script>
<style scoped>
.module-permission-group:last-child,
.no-permission-found {
    min-height: 55vh;
}
</style>
